import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as procService from "../../services/procService";
import { useModule } from "../Platform/ModuleGate";
import { hasModulePermission } from "../../constants/platformModules";
import { ChatPanel } from "./Chat";
import AwardTab from "./AwardTab";
import { DataTable, ProcBadge, act, amount, csvStamp, downloadCsv, fileSize, linkBtn, openSigned, quoteKey, rfqKey, today } from "./procUi";
import { DetailGrid, EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, cardStyle, formatDate, formatDateTime, pageStyle, useLoad } from "../../components/ui";

// One RFQ (buyer side): what was asked, who it went to, the quotations that came back, and what happened.

const SUPPLIER_STATUS = { 3: "Awarded", 14: "Not awarded", 0: "Not sent", 1: "Invited", 2: "Quotation sent", 4: "Declined or rejected", 5: "Asked for information", 11: "Shortlisted", 12: "Closed, no quotation", 13: "Under evaluation" };
const supplierStatusLabel = (s) => (s.declined_at ? "Declined" : SUPPLIER_STATUS[s.status] || String(s.status));
const supplierBadge = (s) => (s.declined_at ? "DECLINED" : s.status === 11 ? "QUALIFIED" : s.status === 4 ? "REJECTED" : s.status === 5 ? "INFO" : s.status === 2 ? "QUOTED" : s.status === 13 ? "EVALUATION" : s.status === 12 ? "CLOSED" : s.status === 3 ? "AWARDED" : s.status === 14 ? "NOT_AWARDED" : "LIVE");

const ACTIVITY = {
  RFQ_CREATED: "RFQ created",
  RFQ_UPDATED: "RFQ changed",
  RFQ_PUBLISHED: "RFQ sent to suppliers",
  SUPPLIER_ADDED: "Suppliers added",
  RFQ_EXTENDED: "Deadline extended",
  RFQ_CANCELLED: "RFQ cancelled",
  RFQ_VIEWED: "Opened the RFQ",
  SUPPLIER_DECLINED: "Declined to quote",
  INFO_REQUESTED: "Asked for information",
  INFO_ANSWERED: "Question marked as answered",
  QUOTATION_SUBMITTED: "Quotation sent",
  QUOTATION_UPDATED: "Quotation revised",
  QUOTATION_QUALIFIED: "Quotation shortlisted",
  QUOTATION_REJECTED: "Quotation rejected",
  QUOTATION_RESET: "Decision taken back",
  RFQ_EVALUATION: "Deadline passed: ready for evaluation",
  RFQ_CLOSED: "Deadline passed: closed without quotations",
  AWARD_SUBMITTED: "Award sent for approval",
  AWARD_APPROVED: "Award approved: purchase orders created",
  AWARD_REJECTED: "Award rejected",
  AWARD_WITHDRAWN: "Award withdrawn",
  ORDER_ACCEPTED: "Order accepted",
  ORDER_REJECTED: "Order rejected",
};
const detailText = (a) => {
  const d = a.details || {};
  if (a.action_type === "RFQ_EXTENDED") return `${formatDate(d.from)} to ${formatDate(d.to)}`;
  if (a.action_type === "QUOTATION_SUBMITTED" || a.action_type === "QUOTATION_UPDATED") return `${d.quotation_number || ""} · ${amount(d.total)}`;
  if (a.action_type === "AWARD_SUBMITTED" || a.action_type === "AWARD_APPROVED") return `${d.award_number} · ${amount(d.total)}`;
  if (a.action_type === "ORDER_ACCEPTED" || a.action_type === "ORDER_REJECTED") return d.reason ? `${d.order_number}: ${d.reason}` : d.order_number;
  if (a.action_type === "RFQ_PUBLISHED") return `${d.rfq_number} to ${d.suppliers} supplier${d.suppliers === 1 ? "" : "s"}`;
  return d.reason || d.note || null;
};

function AddSuppliers({ rfq, onHide, onDone }) {
  const eligible = useLoad(() => procService.getEligibleSuppliers([]), []);
  const [picked, setPicked] = useState([]);
  const have = new Set(rfq.suppliers.map((s) => `${s.supplier_name}|${s.company_name}`));
  const rows = (eligible.data || []).filter((s) => !have.has(`${s.supplier_name}|${s.company_name}`));
  const key = (s) => `${s.supplier_name}|${s.company_name}`;
  const save = async () => {
    const ids = rows.filter((s) => picked.includes(key(s))).map((s) => s.supplier_id);
    const res = await act(() => procService.addRfqSuppliers(rfq.id, ids), "Suppliers added");
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title="Add suppliers" width={460} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={!picked.length}>Add</PrimaryButton></>}>
      <LoadState loading={eligible.loading} error={eligible.error} onRetry={eligible.reload}>
        {rows.length === 0 ? <EmptyState title="Everyone is already on this RFQ" /> : null}
        {rows.map((s) => (
          <label key={key(s)} className="d-flex gap-2 mb-2" style={{ fontSize: 14 }}>
            <input type="checkbox" className="form-check-input mt-1" checked={picked.includes(key(s))} onChange={() => setPicked((p) => (p.includes(key(s)) ? p.filter((k) => k !== key(s)) : [...p, key(s)]))} aria-label={s.supplier_name} />
            <span>
              <strong>{s.supplier_name}</strong>
              <div style={{ fontSize: 12, color: "#6B7280" }}>{s.categories.map((c) => c.name).join(", ") || "No categories set"}</div>
            </span>
          </label>
        ))}
        {rfq.status === 1 ? <div style={{ fontSize: 12, color: "#6B7280" }}>Suppliers you add are invited straight away.</div> : null}
      </LoadState>
    </SlidePanel>
  );
}

function QuotationCompare({ rfq, canManage, onChanged }) {
  const { data, loading, error, reload } = useLoad(() => procService.getRfqQuotations(rfq.id), [rfq.id, rfq.quotation_count]);
  const [rejecting, setRejecting] = useState(null);
  const reviewable = rfq.status === 1 || rfq.status === 13;
  const changed = () => {
    reload();
    onChanged();
  };
  const decide = (q, action) => act(() => procService.reviewQuotation(rfq.id, q.id, action), "Saved").then((r) => r && changed());
  const quotes = (data && data.quotations) || [];
  const lines = (data && data.lines) || [];
  const lowest = (data && data.lowest) || [];
  const exportCsv = () =>
    downloadCsv(
      `${rfq.rfq_number || "rfq"}-comparison-${csvStamp()}.csv`,
      ["Item", "Quantity", "Unit", ...quotes.map((q) => `${q.company_name} (${q.quotation_number})`)],
      [
        ...lines.map((l) => [l.item, l.quantity, l.uom || "", ...quotes.map((q) => { const p = q.prices.find((x) => x.line_no === l.line_no); return p ? p.price : ""; })]),
        ["Total", "", data.rfq.currency, ...quotes.map((q) => q.total)],
        ["Delivery time", "", "", ...quotes.map((q) => q.delivery_time || "")],
        ["Prices valid until", "", "", ...quotes.map((q) => q.quotation_validity || "")],
        ["Payment terms", "", "", ...quotes.map((q) => q.payment_terms || "")],
      ]
    );

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {quotes.length === 0 ? (
        <EmptyState title="No quotations yet" text={rfq.status === 0 ? "Quotations appear here once suppliers respond to the published RFQ." : "Suppliers' quotations appear here as they send them."} />
      ) : (
        <>
          <div className="d-flex align-items-center mb-2"><h6 className="mb-0">Compare prices</h6><span className="ms-auto"><SecondaryButton onClick={exportCsv}>Export CSV</SecondaryButton></span></div>
          <DataTable
            minWidth={320 + quotes.length * 160}
            rowKey={(r) => r.line_no}
            rows={lines}
            columns={[
              { label: "Item", render: (l) => <><div style={{ fontWeight: 600 }}>{l.item}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{l.quantity} {l.uom || ""}{l.price ? ` · estimate ${amount(l.price)}` : ""}</div></> },
              ...quotes.map((q) => ({
                label: q.company_name,
                render: (l) => {
                  const p = q.prices.find((x) => x.line_no === l.line_no);
                  if (!p) return <span style={{ color: "#9CA3AF" }}>not quoted</span>;
                  const best = lowest.some((x) => x.line_no === l.line_no && x.quotation_number === q.quotation_number);
                  return (
                    <span style={best ? { color: "#065F46", fontWeight: 700 } : undefined} title={best ? "Lowest price" : undefined}>
                      {amount(p.price)}
                      {p.quantity !== l.quantity ? <span style={{ fontSize: 12, color: "#6B7280" }}> for {p.quantity}</span> : null}
                    </span>
                  );
                },
              })),
            ]}
            footer={
              <tr>
                <th style={{ padding: "10px 12px" }}>Total ({data.rfq.currency})</th>
                {quotes.map((q) => (
                  <th key={q.quotation_number} style={{ padding: "10px 12px" }}>{amount(q.total)}</th>
                ))}
              </tr>
            }
          />
          <h6 className="mt-4">Quotations</h6>
          {quotes.map((q) => (
            <div key={q.quotation_number} style={{ ...cardStyle, padding: 16, marginBottom: 12 }}>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <strong>{q.company_name}</strong>
                <span style={{ color: "#6B7280", fontSize: 13 }}>{q.quotation_number}{q.revision > 1 ? ` · revision ${q.revision}` : ""}</span>
                <ProcBadge status={quoteKey(q.status)} label={q.status === 2 ? "Submitted" : undefined} />
                {q.compliance !== "COMPLETE" ? <ProcBadge status={q.compliance} label={q.compliance === "EXPIRED" ? "A supplier document expired" : "Supplier documents pending"} /> : null}
                <strong className="ms-auto">{data.rfq.currency} {amount(q.total)}</strong>
              </div>
              <DetailGrid
                columns={3}
                items={[
                  ["Delivery time", q.delivery_time],
                  ["Prices valid until", q.quotation_validity ? formatDate(q.quotation_validity) : null],
                  ["Payment terms", q.payment_terms],
                  ["Sent", formatDateTime(q.submitted_at)],
                  ["Lines quoted", `${q.lines_quoted} of ${lines.length}`],
                  ["Note", q.additional_info],
                ]}
              />
              {q.documents.length ? (
                <div className="mt-2" style={{ fontSize: 13 }}>
                  {q.documents.map((doc) => (
                    <span key={doc.id} className="me-3">
                      {doc.document}:{" "}
                      <button type="button" style={linkBtn} onClick={() => openSigned(() => procService.getQuotationDocumentUrl(doc.id), toast)}>
                        {doc.file_name}
                      </button>{" "}
                      <span style={{ color: "#6B7280" }}>{fileSize(doc.size)}</span>
                    </span>
                  ))}
                </div>
              ) : null}
              {q.missing_documents.length ? <div className="mt-2" style={{ fontSize: 13, color: "#92400E" }}>Missing: {q.missing_documents.join(", ")}</div> : null}
              {q.status === 4 && q.review_note ? <div className="mt-2" style={{ fontSize: 13, color: "#991B1B" }}>Rejected: {q.review_note}</div> : null}
              {canManage && reviewable ? (
                <div className="d-flex gap-2 mt-3">
                  {q.status !== 11 ? <PrimaryButton onClick={() => decide(q, "QUALIFY")}>Shortlist</PrimaryButton> : null}
                  {q.status !== 4 ? <SecondaryButton onClick={() => setRejecting(q)}>Reject</SecondaryButton> : null}
                  {q.status === 11 || q.status === 4 ? <SecondaryButton onClick={() => decide(q, "RESET")}>Take decision back</SecondaryButton> : null}
                </div>
              ) : null}
            </div>
          ))}
        </>
      )}
      <FormDrawer
        show={!!rejecting}
        title="Reject quotation"
        submitLabel="Reject"
        initial={{}}
        onHide={() => setRejecting(null)}
        onSave={(clean) => procService.reviewQuotation(rfq.id, rejecting.id, "REJECT", clean.note)}
        onDone={changed}
        fields={[{ key: "note", label: `Why is ${rejecting ? rejecting.company_name : "this"} quotation rejected?`, type: "textarea", required: true, max: 500, hint: "The supplier sees this reason" }]}
      />
    </LoadState>
  );
}

export default function RfqDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { access } = useModule();
  const canManage = hasModulePermission(access, "PROC", "PROC_ADMIN", "PROC_BUYER");
  const [tab, setTab] = useState("overview");
  const [extending, setExtending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [adding, setAdding] = useState(false);
  const [chat, setChat] = useState(null); // the supplier row being written to
  const { data: rfq, loading, error, reload } = useLoad(() => procService.getRfq(id), [id]);
  const activity = useLoad(() => procService.getRfqActivity(id), [id, tab], { enabled: tab === "activity" });

  const publish = () => act(() => procService.publishRfq(id), "RFQ sent to suppliers").then((r) => r && reload());
  const answered = (s) => act(() => procService.markInfoAnswered(id, s.supplier_id), "Saved").then((r) => r && reload());

  const reopenable = rfq && (rfq.status === 13 || (rfq.status === 12 && rfq.closed_reason === "NO_QUOTES"));
  const actions = rfq && canManage ? (
    <>
      {rfq.status === 0 || rfq.status === 1 ? <SecondaryButton onClick={() => navigate(`/proc/rfqs/${id}/edit`)}>Change</SecondaryButton> : null}
      {rfq.status === 0 ? <PrimaryButton onClick={publish}>Publish to suppliers</PrimaryButton> : null}
      {rfq.status === 1 || reopenable ? <SecondaryButton onClick={() => setExtending(true)}>{reopenable ? "Reopen with a new deadline" : "Extend deadline"}</SecondaryButton> : null}
      {rfq.status !== 12 ? <SecondaryButton onClick={() => setCancelling(true)}>Cancel RFQ</SecondaryButton> : null}
    </>
  ) : null;

  const tabs = rfq
    ? [
        { key: "overview", label: "Overview" },
        { key: "suppliers", label: "Suppliers", badge: rfq.suppliers.length || undefined },
        { key: "quotations", label: "Quotations", badge: rfq.quotation_count || undefined },
        { key: "award", label: "Award" },
        { key: "activity", label: "Activity" },
      ]
    : [];

  return (
    <div style={pageStyle}>
      <PageHeader title={rfq ? rfq.title : "RFQ"} subtitle={rfq ? rfq.rfq_number || "Draft: not sent yet" : ""} actions={<span className="d-flex gap-2 flex-wrap align-items-center">{rfq ? <ProcBadge status={rfqKey(rfq)} label={rfq.status_label} /> : null}{actions}</span>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rfq ? (
          <>
            <Tabs tabs={tabs} active={tab} onChange={setTab} />

            {tab === "overview" ? (
              <>
                <div style={{ ...cardStyle, padding: 16 }} className="mb-4">
                  <DetailGrid
                    columns={3}
                    items={[
                      ["Awarded", { LUMPSUM: "Lumpsum, to one supplier", ITEM: "Item based", SPLIT: `Split (smallest share ${rfq.min_split_pct}%)` }[rfq.rfq_type]],
                      ["Suppliers must quote by", rfq.responsible_date ? formatDate(rfq.responsible_date) : null],
                      ["Delivery date", rfq.delivery_date ? formatDate(rfq.delivery_date) : null],
                      ["Delivery location", rfq.location],
                      ["Created by", rfq.created_by_name],
                      ["Sent", rfq.published_at ? formatDateTime(rfq.published_at) : "Not yet"],
                      ["Instructions", rfq.instructions],
                    ]}
                  />
                </div>
                <h6>Items</h6>
                <DataTable
                  minWidth={620}
                  rowKey={(l) => l.line_no}
                  rows={rfq.lines}
                  empty={<EmptyState title="No items yet" action={canManage ? <PrimaryButton onClick={() => navigate(`/proc/rfqs/${id}/edit`)}>Add items</PrimaryButton> : null} />}
                  columns={[
                    { label: "#", render: (l) => l.line_no, width: 50 },
                    { label: "Item", render: (l) => <><div style={{ fontWeight: 600 }}>{l.item}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{[l.item_sku, l.category_name, l.brand].filter(Boolean).join(" · ")}</div></> },
                    { label: "Quantity", render: (l) => `${l.quantity} ${l.uom || ""}` },
                    { label: "Your estimate", render: (l) => (l.price ? amount(l.price) : "—") },
                    { label: "Specification", render: (l) => l.description || "—" },
                    {
                      label: "Files",
                      render: (l) => {
                        const mine = rfq.attachments.filter((f) => f.owner_type === "RFQ_LINE_ATTACHMENT" && f.line_no === l.line_no);
                        return mine.length ? mine.map((f) => <div key={f.id}><button type="button" style={linkBtn} onClick={() => openSigned(() => procService.getRfqAttachmentUrl(id, f.id), toast)}>{f.file_name}</button></div>) : "—";
                      },
                    },
                  ]}
                />
                {rfq.required_documents.length ? (
                  <>
                    <h6 className="mt-4">Documents asked with the quotation</h6>
                    <ul style={{ fontSize: 14 }}>
                      {rfq.required_documents.map((n) => (
                        <li key={n.id}>{n.document}{n.is_mandatory ? " (required)" : ""}{n.rfq_param ? `: ${n.rfq_param}` : ""}</li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {rfq.attachments.some((f) => f.owner_type === "RFQ_ATTACHMENT") ? (
                  <>
                    <h6 className="mt-4">Files for suppliers</h6>
                    {rfq.attachments.filter((f) => f.owner_type === "RFQ_ATTACHMENT").map((f) => (
                      <div key={f.id} style={{ fontSize: 14 }}>
                        <button type="button" style={linkBtn} onClick={() => openSigned(() => procService.getRfqAttachmentUrl(id, f.id), toast)}>{f.file_name}</button> <span style={{ color: "#6B7280" }}>{fileSize(f.size)}</span>
                      </div>
                    ))}
                  </>
                ) : null}
              </>
            ) : null}

            {tab === "suppliers" ? (
              <>
                {canManage && (rfq.status === 0 || rfq.status === 1) ? <div className="mb-3"><SecondaryButton onClick={() => setAdding(true)}>Add suppliers</SecondaryButton></div> : null}
                <DataTable
                  minWidth={680}
                  rowKey={(s) => `${s.supplier_name}|${s.company_name}`}
                  rows={rfq.suppliers}
                  empty={<EmptyState title="No suppliers chosen" text="Choose the suppliers who should receive this RFQ." />}
                  columns={[
                    { label: "Supplier", render: (s) => <><div style={{ fontWeight: 600 }}>{s.supplier_name}</div>{s.company_name !== s.supplier_name ? <div style={{ fontSize: 12, color: "#6B7280" }}>{s.company_name}</div> : null}</> },
                    { label: "Status", render: (s) => <ProcBadge status={supplierBadge(s)} label={supplierStatusLabel(s)} /> },
                    { label: "Opened", render: (s) => (s.viewed_at ? formatDateTime(s.viewed_at) : "Not yet") },
                    { label: "Quotation", render: (s) => (s.quotation_id ? `${s.quotation_number} · ${amount(s.quotation_total)}` : "—") },
                    {
                      label: "",
                      render: (s) => (
                        rfq.status === 0 ? null : (
                          <button type="button" style={linkBtn} onClick={(e) => { e.stopPropagation(); setChat(s); }} aria-label={`Message ${s.supplier_name}`}>
                            Message
                          </button>
                        )
                      ),
                    },
                    {
                      label: "Note",
                      render: (s) => (
                        <>
                          {s.decline_reason ? <div style={{ fontSize: 13 }}>{s.decline_reason}</div> : null}
                          {s.info_request ? (
                            <div style={{ fontSize: 13 }}>
                              “{s.info_request}”{" "}
                              {canManage ? <button type="button" style={linkBtn} onClick={() => answered(s)}>Mark answered</button> : null}
                            </div>
                          ) : null}
                          {!s.decline_reason && !s.info_request ? "—" : null}
                        </>
                      ),
                    },
                  ]}
                />
              </>
            ) : null}

            {tab === "quotations" ? <QuotationCompare rfq={rfq} canManage={canManage} onChanged={reload} /> : null}

            {tab === "award" ? <AwardTab rfq={rfq} canManage={canManage} onChanged={reload} /> : null}

            {tab === "activity" ? (
              <LoadState loading={activity.loading} error={activity.error} onRetry={activity.reload}>
                <DataTable
                  minWidth={520}
                  rows={activity.data || []}
                  empty={<EmptyState title="Nothing has happened yet" />}
                  columns={[
                    { label: "When", render: (a) => formatDateTime(a.created_at), style: { whiteSpace: "nowrap" } },
                    { label: "What", render: (a) => ACTIVITY[a.action_type] || a.action_type },
                    { label: "Who", render: (a) => (a.user_role === "SYSTEM" ? "System" : a.user_role === "SUPPLIER" ? a.supplier_name : a.actor_name) },
                    { label: "Details", render: (a) => detailText(a) },
                  ]}
                />
              </LoadState>
            ) : null}

            {chat ? <ChatPanel service={procService} refQuery={{ supplier_id: chat.supplier_id, rfq_id: rfq.id }} title={chat.supplier_name} subtitle={`${rfq.rfq_number} · ${rfq.title}`} canWrite={canManage} onClose={() => { setChat(null); reload(); }} /> : null}
            {adding ? <AddSuppliers rfq={rfq} onHide={() => setAdding(false)} onDone={reload} /> : null}
            <FormDrawer
              show={extending}
              title={reopenable ? "Reopen the RFQ" : "Extend the deadline"}
              submitLabel={reopenable ? "Reopen" : "Extend"}
              initial={{ responsible_date: rfq.responsible_date && rfq.responsible_date > today() ? rfq.responsible_date : today() }}
              onHide={() => setExtending(false)}
              onSave={(clean) => procService.extendRfq(id, clean.responsible_date)}
              onDone={reload}
              fields={[{ key: "responsible_date", label: "Suppliers can quote until", type: "date", required: true, hint: "Suppliers are told about the new date" }]}
            />
            <FormDrawer
              show={cancelling}
              title="Cancel this RFQ"
              submitLabel="Confirm cancellation"
              initial={{}}
              onHide={() => setCancelling(false)}
              onSave={(clean) => procService.cancelRfq(id, clean.reason)}
              onDone={reload}
              fields={[{ key: "reason", label: "Reason (optional)", type: "textarea", max: 500, hint: rfq.status === 0 ? "Nobody has received this draft" : "The suppliers are told the RFQ is cancelled" }]}
            />
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
